'use client';

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';

interface ImageComparisonSliderProps {
  beforeImage: string;
  afterImage: string;
  beforeAlt?: string;
  afterAlt?: string;
  className?: string;
  initialPosition?: number;
  aspectRatio?: number;
  /** 'cover' for cards (default), 'contain' for fullscreen viewer */
  objectFit?: 'cover' | 'contain';
  onPositionChange?: (position: number) => void;
  onDraggingChange?: (isDragging: boolean) => void;
  /** External programmatic target position (0..100, LOGICAL semantic —
   *  0 = after/render fully visible, 100 = before/source fully visible).
   *  RTL is handled entirely inside the slider (mask side, handle x, drag
   *  mapping) so callers always think in start→finish terms, never in
   *  left/right pixels — that's what stays consistent across locales.
   *  When provided, the slider animates to this position on every change,
   *  unless the user has manually dragged the handle (manual override
   *  resets when this prop changes again). */
  programmaticPosition?: number;
}

export function ImageComparisonSlider({
  beforeImage,
  afterImage,
  beforeAlt = 'Before',
  afterAlt = 'After',
  className,
  initialPosition = 0,
  aspectRatio = 1.333,
  objectFit = 'cover',
  onPositionChange,
  onDraggingChange,
  programmaticPosition,
}: ImageComparisonSliderProps) {
  const t = useTranslations('common');
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  // Drives smooth nudge animation from docked edge to centre on tap.
  // Plain drag must NOT animate, otherwise the handle visibly lags the cursor.
  const [isAnimating, setIsAnimating] = useState(false);
  const [beforeLoaded, setBeforeLoaded] = useState(false);
  const [afterLoaded, setAfterLoaded] = useState(false);
  // Keep the outgoing render painted beneath the incoming one until it loads,
  // so paging between ready renders is a straight render→render swap with no
  // flash of the source (the base layer) or the loading overlay. Once a render
  // has been shown at least once, the overlay never returns — the buffer covers
  // every later swap. Mirrors Apple/Android double-buffering.
  const [previousAfter, setPreviousAfter] = useState<string | null>(null);
  const [hasShownAfterOnce, setHasShownAfterOnce] = useState(false);
  const displayedAfterRef = useRef(afterImage);
  const [imageNaturalSize, setImageNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  // Distinguish tap vs drag: track press start and whether the finger has moved
  // past the threshold. A tap on a docked handle nudges the slider to the centre
  // as a discoverability hint (users were clicking the grip instead of dragging).
  const pressStartXRef = useRef<number | null>(null);
  const didMoveRef = useRef(false);
  const nudgeTimerRef = useRef<number | null>(null);

  // Detect RTL from document direction
  const [isRTL, setIsRTL] = useState(false);
  useEffect(() => {
    setIsRTL(document.documentElement.dir === 'rtl');
  }, []);

  // Track container size for fitted height calculation
  useEffect(() => {
    const container = containerRef.current;
    if (!container || objectFit !== 'contain') return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [objectFit]);

  // Fitted image rect within the container (contain mode) — the actual painted
  // box after object-contain letterboxes the image. The seam, handle, clip masks
  // and drag mapping are all expressed relative to THIS rect (not the full
  // container), so the divider tracks the image instead of the black bars.
  // Same fit math as Apple/Android.
  const fittedRect = useMemo(() => {
    if (objectFit !== 'contain' || !imageNaturalSize || !containerSize) {
      return null;
    }
    const imageAspect = imageNaturalSize.width / imageNaturalSize.height;
    const containerAspect = containerSize.width / containerSize.height;
    let width: number;
    let height: number;
    if (imageAspect > containerAspect) {
      // Image is wider — width-limited, top/bottom letterbox bars.
      width = containerSize.width;
      height = containerSize.width / imageAspect;
    } else {
      // Image is taller/narrower — height-limited, left/right letterbox bars.
      height = containerSize.height;
      width = containerSize.height * imageAspect;
    }
    return {
      width,
      height,
      offsetX: (containerSize.width - width) / 2,
      offsetY: (containerSize.height - height) / 2,
    };
  }, [objectFit, imageNaturalSize, containerSize]);
  const fittedHeight = fittedRect?.height ?? null;

  // Reset each loaded flag only when *its own* URL changes. Resetting both on either
  // change leaves the unchanged-side flag stuck at false: next/image keeps the same DOM
  // <img> when src is identical and never re-fires onLoad — so the loading overlay would
  // hang forever when paginating renders within one derivative angle (sourceUrl is shared,
  // only afterImage changes).
  useEffect(() => {
    setBeforeLoaded(false);
  }, [beforeImage]);

  useEffect(() => {
    setAfterLoaded(false);
    // Promote the currently-displayed render to the buffer so it stays painted
    // beneath the incoming one while it decodes (cleared on the new onLoad).
    if (displayedAfterRef.current !== afterImage) {
      setPreviousAfter(displayedAfterRef.current);
      displayedAfterRef.current = afterImage;
    }
  }, [afterImage]);

  // Reset to initialPosition when the image pair changes. `position` is LOGICAL
  // (0 = render visible, 100 = source visible) so it doesn't depend on locale —
  // dropping the old RTL flip removes the "physical-vs-logical" mismatch with
  // programmaticPosition that desynced the mode toggle from the slider in Arabic/Hebrew.
  useEffect(() => {
    setPosition(initialPosition);
  }, [beforeImage, afterImage]); // initialPosition only used on image change

  // Programmatic position control (mode-driven from outside).
  // - `userOverriddenRef` flips to true the first time the user drags;
  //   programmatic updates are ignored until the prop *changes* again
  //   (mode toggle on the parent resets the override).
  // - Animation uses the same 450ms cubic-bezier as RenderModeToggle so
  //   the two surfaces stay in lockstep when mode flips.
  const userOverriddenRef = useRef(false);
  const lastProgrammaticPositionRef = useRef<number | undefined>(
    programmaticPosition
  );
  const [isProgrammaticallyAnimating, setIsProgrammaticallyAnimating] =
    useState(false);
  useEffect(() => {
    if (programmaticPosition === undefined) return;
    // Each new prop value clears any prior user override — a mode toggle
    // should always drive the slider, even if the user dragged manually
    // since the previous mode flip.
    if (lastProgrammaticPositionRef.current !== programmaticPosition) {
      userOverriddenRef.current = false;
      lastProgrammaticPositionRef.current = programmaticPosition;
    }
    if (userOverriddenRef.current) return;
    // programmaticPosition is already LOGICAL — copy through without flipping;
    // RTL mirroring lives in the mask and handle below.
    setIsProgrammaticallyAnimating(true);
    setPosition(programmaticPosition);
    onPositionChange?.(programmaticPosition);
    const timer = window.setTimeout(() => {
      setIsProgrammaticallyAnimating(false);
    }, 470);
    return () => window.clearTimeout(timer);
  }, [programmaticPosition, onPositionChange]);

  const handleMove = useCallback(
    (clientX: number) => {
      if (!containerRef.current) return;

      // Drag interrupts any in-flight nudge animation so the handle follows
      // the finger exactly instead of lagging behind a CSS transition.
      if (nudgeTimerRef.current != null) {
        window.clearTimeout(nudgeTimerRef.current);
        nudgeTimerRef.current = null;
        setIsAnimating(false);
      }
      // User dragged → ignore programmatic position until the prop changes
      // again (i.e. parent toggles mode).
      userOverriddenRef.current = true;
      setIsProgrammaticallyAnimating(false);

      const rect = containerRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      // Physical position from the left edge (container is force-LTR). In contain
      // mode the image is letterboxed, so map against the FITTED image rect (not
      // the full container) — dragging over the black bars clamps to the edges
      // and the seam tracks the image, not the window.
      const physical =
        fittedRect && fittedRect.width > 0
          ? Math.max(
              0,
              Math.min(100, ((x - fittedRect.offsetX) / fittedRect.width) * 100)
            )
          : Math.max(0, Math.min(100, (x / rect.width) * 100));
      // Convert to LOGICAL (0 = render visible, 100 = source visible).
      // RTL reveals source from the right, so dragging the handle leftward
      // reveals more source → logical = 100 - physical.
      const logical = isRTL ? 100 - physical : physical;

      setPosition(logical);
      onPositionChange?.(logical);
    },
    [onPositionChange, isRTL, fittedRect]
  );

  // Drag-vs-tap threshold (px). Below this, the press is considered a tap.
  const TAP_MOVEMENT_THRESHOLD = 3;
  // Nudge animation duration (ms). Keep in sync with the inline CSS transition below.
  const NUDGE_DURATION_MS = 300;

  const finishPress = useCallback(
    (clientX: number | null) => {
      const pressStartX = pressStartXRef.current;
      pressStartXRef.current = null;

      // Tap detected: press started, finger barely moved.
      if (
        clientX != null &&
        pressStartX != null &&
        !didMoveRef.current &&
        Math.abs(clientX - pressStartX) <= TAP_MOVEMENT_THRESHOLD
      ) {
        // Only nudge when the handle is essentially docked at an edge.
        // The visible half-circle peeking from the side reads as "not interactive"
        // to many users; auto-sliding to the centre teaches the affordance.
        const isDockedAtStart = position < 0.5;
        const isDockedAtEnd = position > 99.5;
        if (isDockedAtStart || isDockedAtEnd) {
          if (nudgeTimerRef.current != null) {
            window.clearTimeout(nudgeTimerRef.current);
          }
          setIsAnimating(true);
          setPosition(50);
          onPositionChange?.(50);
          // Keep isDragging/labels visible for the duration of the animation,
          // reinforcing that the slider is the comparison control.
          nudgeTimerRef.current = window.setTimeout(() => {
            nudgeTimerRef.current = null;
            setIsAnimating(false);
            setIsDragging(false);
            onDraggingChange?.(false);
          }, NUDGE_DURATION_MS + 20);
          return;
        }
      }

      setIsDragging(false);
      onDraggingChange?.(false);
    },
    [position, onPositionChange, onDraggingChange]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      onDraggingChange?.(true);
      pressStartXRef.current = e.clientX;
      didMoveRef.current = false;
    },
    [onDraggingChange]
  );

  const handleTouchStartReact = useCallback((e: React.TouchEvent) => {
    // Don't call preventDefault() here - React events use passive listeners
    // Native listener with passive: false handles preventDefault()
    setIsDragging(true);
    if (e.touches[0]) {
      pressStartXRef.current = e.touches[0].clientX;
      didMoveRef.current = false;
    }
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      const start = pressStartXRef.current;
      if (start != null && Math.abs(e.clientX - start) > TAP_MOVEMENT_THRESHOLD) {
        didMoveRef.current = true;
      }
      if (didMoveRef.current) {
        handleMove(e.clientX);
      }
    },
    [isDragging, handleMove]
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      finishPress(e.clientX);
    },
    [finishPress]
  );

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      // Prevent browser's default swipe-to-navigate behavior
      e.preventDefault();
      setIsDragging(true);
      onDraggingChange?.(true);

      if (e.touches[0]) {
        pressStartXRef.current = e.touches[0].clientX;
        didMoveRef.current = false;
      }
    },
    [onDraggingChange]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging || !e.touches[0]) return;
      if (e.cancelable) {
        e.preventDefault();
      }
      const start = pressStartXRef.current;
      if (
        start != null &&
        Math.abs(e.touches[0].clientX - start) > TAP_MOVEMENT_THRESHOLD
      ) {
        didMoveRef.current = true;
      }
      if (didMoveRef.current) {
        handleMove(e.touches[0].clientX);
      }
    },
    [isDragging, handleMove]
  );

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      const lastTouch = e.changedTouches[0];
      finishPress(lastTouch ? lastTouch.clientX : null);
    },
    [finishPress]
  );

  // Cancel pending nudge timer on unmount to avoid setState on a dead component.
  useEffect(
    () => () => {
      if (nudgeTimerRef.current != null) {
        window.clearTimeout(nudgeTimerRef.current);
      }
    },
    []
  );

  // Handle dragging events
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
    return undefined;
  }, [
    isDragging,
    handleMouseMove,
    handleMouseUp,
    handleTouchMove,
    handleTouchEnd,
  ]);

  // Attach non-passive touch listener to handle
  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;

    handle.addEventListener('touchstart', handleTouchStart, { passive: false });

    return () => {
      handle.removeEventListener('touchstart', handleTouchStart);
    };
  }, [handleTouchStart]);

  const isReady = beforeLoaded && afterLoaded;
  // Slider chrome (handle + divider) stays visible across render swaps: once a
  // render has loaded, the after side briefly re-loading on a carousel step
  // must not blink the handle off (the previous-render buffer keeps the picture
  // on screen, so the chrome should stay too).
  const chromeReady = isReady || (beforeLoaded && hasShownAfterOnce);

  // Don't apply aspectRatio when parent defines dimensions (h-full means parent controls height)
  const parentDefinesDimensions = className?.includes('h-full');
  const objectFitClass =
    objectFit === 'contain' ? 'object-contain' : 'object-cover';

  // `position` is LOGICAL (0 = render visible, 100 = source visible).
  // LTR: source is revealed from the left, so clip the after image from the
  // left by `position%`. RTL: source is revealed from the right, so clip from
  // the right by `position%`. Either way p=0 → no clip → render fully visible,
  // p=100 → fully clipped → source fully visible.
  // Seam position. In contain mode the divider must sit on the fitted image box,
  // so express the clip masks + handle in px relative to that rect. `P` is the
  // physical seam fraction from the left edge of the active region.
  const P = isRTL ? 100 - position : position;
  const seamX =
    fittedRect && containerSize
      ? fittedRect.offsetX + (P / 100) * fittedRect.width
      : null;
  const rightInsetPx =
    seamX != null && containerSize ? containerSize.width - seamX : null;

  const afterClipPath =
    seamX != null && rightInsetPx != null
      ? isRTL
        ? `inset(0 ${rightInsetPx}px 0 0)`
        : `inset(0 0 0 ${seamX}px)`
      : isRTL
        ? `inset(0 ${position}% 0 0)`
        : `inset(0 0 0 ${position}%)`;

  // Before(source) is clipped to ONLY its revealed strip — the complement of
  // the after(render) clip — so the source is never painted underneath the
  // render. At p=0 it clips to nothing → no source pixels to flash through
  // during a render swap (mirrors Apple's before-image mask / Android's
  // before clipRect; keeps the principle uniform across the 3 fronts).
  const beforeClipPath =
    seamX != null && rightInsetPx != null
      ? isRTL
        ? `inset(0 0 0 ${seamX}px)`
        : `inset(0 ${rightInsetPx}px 0 0)`
      : isRTL
        ? `inset(0 0 0 ${100 - position}%)`
        : `inset(0 ${100 - position}% 0 0)`;

  // Label positions: in RTL, before is on right and after is on left
  const leftLabel = isRTL ? t('after') : t('before');
  const rightLabel = isRTL ? t('before') : t('after');
  const showLeftLabel = isRTL ? position < 95 : position > 5;
  const showRightLabel = isRTL ? position > 5 : position < 95;

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative overflow-hidden select-none',
        isDragging && 'cursor-grabbing',
        className
      )}
      style={{
        ...(!parentDefinesDimensions && {
          aspectRatio: aspectRatio.toString(),
        }),
        touchAction: 'pan-y',
        direction: 'ltr', // Force LTR for consistent physical positioning
      }}
    >
      {/* Before Image (base layer) — clipped to its revealed strip only.
          Its clip-path animates with the SAME transition as the after layer so
          the two complementary masks move in lockstep on a mode flip. Without
          this the before clip jumped instantly while the after clip eased over
          450ms: on Explore→Refine the source vanished a frame before the render
          wiped in, flashing the empty container white in the gap (the reverse
          looked clean only because the source reappears instantly beneath the
          out-going render). */}
      <div
        className="absolute inset-0"
        style={{
          clipPath: beforeClipPath,
          transition: isAnimating
            ? 'clip-path 300ms ease-out'
            : isProgrammaticallyAnimating
              ? 'clip-path 450ms cubic-bezier(0.65, 0, 0.35, 1)'
              : undefined,
        }}
      >
        <Image
          src={beforeImage}
          alt={beforeAlt}
          fill
          sizes="(max-width: 768px) 100vw, 768px"
          className={objectFitClass}
          priority
          onLoad={(e) => {
            setBeforeLoaded(true);
            const img = e.currentTarget as HTMLImageElement;
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
              setImageNaturalSize({
                width: img.naturalWidth,
                height: img.naturalHeight,
              });
            }
          }}
        />
        {/* Left label — shown only when dragging */}
        {isReady && showLeftLabel && (
          <div
            className={cn(
              'absolute bottom-2 left-2 z-10 transition-opacity duration-200',
              isDragging ? 'opacity-100' : 'opacity-0'
            )}
          >
            <span className="bg-black/60 text-white text-chip px-2 py-1 rounded backdrop-blur-sm">
              {leftLabel}
            </span>
          </div>
        )}
      </div>

      {/* After Image — clipped based on position */}
      <div
        className="absolute inset-0"
        style={{
          clipPath: afterClipPath,
          transition: isAnimating
            ? 'clip-path 300ms ease-out'
            : isProgrammaticallyAnimating
              ? 'clip-path 450ms cubic-bezier(0.65, 0, 0.35, 1)'
              : undefined,
        }}
      >
        {/* Previous render kept beneath the incoming one during decode so the
            swap reads as a straight render→render replace (no source flash). */}
        {previousAfter && previousAfter !== afterImage && (
          <Image
            src={previousAfter}
            alt=""
            fill
            sizes="(max-width: 768px) 100vw, 768px"
            className={objectFitClass}
            priority
          />
        )}
        <Image
          src={afterImage}
          alt={afterAlt}
          fill
          sizes="(max-width: 768px) 100vw, 768px"
          className={objectFitClass}
          priority
          onLoad={() => {
            setAfterLoaded(true);
            setHasShownAfterOnce(true);
            // New render is painted — drop the previous-render buffer.
            setPreviousAfter(null);
          }}
        />
        {/* Right label — shown only when dragging */}
        {isReady && showRightLabel && (
          <div
            className={cn(
              'absolute bottom-2 right-2 z-10 transition-opacity duration-200',
              isDragging ? 'opacity-100' : 'opacity-0'
            )}
          >
            <span className="bg-black/60 text-white text-chip px-2 py-1 rounded backdrop-blur-sm">
              {rightLabel}
            </span>
          </div>
        )}
      </div>

      {/* Slider Handle */}
      {chromeReady && (
        <div
          ref={handleRef}
          // Overlay tools that draw on the image (edit regions) leave this alone.
          data-image-control
          className={cn(
            'absolute w-1 shadow-lg cursor-col-resize z-10',
            // Hide vertical divider at extreme positions — only the handle semicircle peeks from the edge (design requirement)
            position > 0.1 && position < 99.9 ? 'bg-[#B0B0B0]' : 'bg-transparent shadow-none',
            !fittedHeight && 'top-0 bottom-0'
          )}
          style={{
            // Container is forced direction:ltr so `left` is from the container's
            // physical left edge. In contain mode use the px seam on the fitted
            // image box; otherwise fall back to the container-relative percent.
            // RTL is already folded into `P`/`seamX` above.
            left: seamX != null ? `${seamX}px` : `${P}%`,
            transform: 'translateX(-50%)',
            touchAction: 'none',
            transition: isAnimating
              ? 'left 300ms ease-out'
              : isProgrammaticallyAnimating
                ? 'left 450ms cubic-bezier(0.65, 0, 0.35, 1)'
                : undefined,
            ...(fittedHeight != null && {
              height: `${fittedHeight}px`,
              top: '50%',
              marginTop: `-${fittedHeight / 2}px`,
            }),
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStartReact}
        >
          {/* Handle Grip — pushed down to ~1/5 from the bottom of the image so the
              user's finger doesn't cover the area they're trying to compare while
              dragging. The vertical divider line still spans the full image height. */}
          <div className="absolute top-[80%] left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="w-10 h-10 bg-[#B0B0B0] rounded-full shadow-lg flex items-center justify-center cursor-grab active:cursor-grabbing">
              {/* Ribbed grip — three horizontal bars the user grabs to drag.
                  Replaces the old chevron arrows, which users tapped instead of dragging. */}
              <div className="flex flex-col gap-[3px]">
                <span className="block w-[14px] h-[2px] rounded-full bg-gray-600" />
                <span className="block w-[14px] h-[2px] rounded-full bg-gray-600" />
                <span className="block w-[14px] h-[2px] rounded-full bg-gray-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading state — only on the very first paint. Once a render has been
          shown, later swaps are covered by the previous-render buffer, so the
          gray overlay must not return on each carousel step. */}
      {!isReady && !hasShownAfterOnce && (
        <div className="absolute inset-0 flex items-center justify-center bg-image-backdrop">
          <div className="text-list-item-subtitle text-muted-foreground">
            {t('loadingImages')}
          </div>
        </div>
      )}
    </div>
  );
}
